import { ReactNode, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { TabelaAcao, ColunaTabelaAcao } from '@/components/common/TabelaAcao'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { formatDate, formatDateTime } from '@/utils/formatters'
import { tokens } from '@/theme'
import type { PublicacaoPoliticaSimulacao } from '@/types/simulacaoComissao'

/** O que toda política versionada tem em comum (`Politica*Response`), com a publicação da P6 (V14). */
export interface PoliticaVersionada extends PublicacaoPoliticaSimulacao {
  id: string
  versao: string
  vigenciaInicio: string
  vigenciaFim: string | null
  vigente: boolean
  origem: string
  criadoPor: string | null
  criadoEm: string
}

export const ENCERRAR_VIGENCIA = 'Encerrar vigência'

/** Texto ao lado dos botões desabilitados para quem propôs (o backend recusa com 403 `APROVACAO_PELO_PROPONENTE`). */
export const TEXTO_PROPONENTE_NAO_DECIDE = 'Você propôs esta versão: aprovar ou rejeitar é de outra pessoa.'

const ROTULO_STATUS_PUBLICACAO: Record<string, string> = {
  RASCUNHO: 'Rascunho',
  PENDENTE_APROVACAO: 'Pendente de aprovação',
  ATIVA: 'Ativa',
  REJEITADA: 'Rejeitada',
  ENCERRADA: 'Encerrada',
}

const ROTULO_OWNER: Record<string, string> = {
  COMERCIAL: 'Comercial',
  FINANCEIRO: 'Financeiro',
  OPERACOES: 'Operações',
  JURIDICO: 'Jurídico',
}

const ehPendente = (p: PoliticaVersionada) => p.statusPublicacao === 'PENDENTE_APROVACAO'

const vigencia = (p: PoliticaVersionada) =>
  `${formatDate(p.vigenciaInicio)} → ${p.vigenciaFim ? formatDate(p.vigenciaFim) : 'aberta'}`

const pessoa = (sub: string | null | undefined, eu: string | null | undefined) =>
  !sub ? '—' : eu && sub === eu ? 'você' : sub.slice(0, 8)

const COR_STATUS: Record<string, 'warning' | 'error' | 'success' | 'default'> = {
  PENDENTE_APROVACAO: 'warning',
  REJEITADA: 'error',
  ATIVA: 'success',
}

/** Selo da situação: só traduz o que veio (`statusPublicacao`, `legado`). */
const Situacao = ({ p }: { p: PoliticaVersionada }) => {
  if (p.legado && p.statusPublicacao === 'ATIVA') {
    return <Chip size="small" variant="outlined" label="Legado — anterior à dupla aprovação" />
  }
  if (!p.statusPublicacao) return <>—</>
  return (
    <Chip
      size="small"
      color={COR_STATUS[p.statusPublicacao] ?? 'default'}
      variant={p.statusPublicacao === 'ATIVA' ? 'filled' : 'outlined'}
      label={ROTULO_STATUS_PUBLICACAO[p.statusPublicacao] ?? p.statusPublicacao}
    />
  )
}

interface SecaoPoliticaProps<T extends PoliticaVersionada> {
  titulo: string
  descricao: string
  rotuloNovo: string
  /** `null` = carregando. */
  itens: T[] | null
  erro: string | null
  /** Colunas de domínio (produto, parceiro, spread…); as de versão/vigência/origem/autor são comuns. */
  colunas: ColunaTabelaAcao<T>[]
  mensagemVazio: string
  onNovaVersao: () => void
  onEncerrar: (item: T) => void
  onTentarNovamente: () => void
  'aria-label': string
  /** Conteúdo extra abaixo do título (avisos da seção). */
  children?: ReactNode
  /**
   * P4: leitura sem ações (quem não é ADMIN vê a política de comissão vigente) — sem
   * "Propor" e sem "Encerrar vigência"; a tabela de vigentes vira tabela simples.
   */
  somenteLeitura?: boolean
  /** P6: quem pode propor (produto/comissão: ADMIN; spread/tributo: FINANCEIRO ou ADMIN). Padrão: `!somenteLeitura`. */
  podePropor?: boolean
  /** Encerrar em emergência é só ADMIN (AN-PP6 §3.1). Padrão: `!somenteLeitura`. */
  podeEncerrar?: boolean
  /** P6: mostra Aprovar/Rejeitar nas propostas pendentes (FINANCEIRO ou ADMIN na porta; o papel exato é do backend). */
  podeDecidir?: boolean
  /** `sub` do usuário logado — o proponente não aprova nem rejeita a própria proposta. */
  usuarioId?: string | null
  onAprovar?: (item: T) => void
  onRejeitar?: (item: T) => void
  /** Revisão PP6-valr (8b): o PRÓPRIO proponente retira a proposta pendente (sem segunda pessoa). */
  onRetirar?: (item: T) => void
  /** Id da proposta em decisão (botões ocupados). */
  decidindo?: string | null
}

/** Tabela sem ação por linha — histórico encerrado e o modo somente leitura. */
const TabelaSimples = <T extends PoliticaVersionada>({
  colunas,
  linhas,
  ariaLabel,
}: {
  colunas: ColunaTabelaAcao<T>[]
  linhas: T[]
  ariaLabel: string
}) => (
  <TableContainer sx={{ mt: 1, overflowX: 'auto' }}>
    <Table size="small" aria-label={ariaLabel}>
      <TableHead>
        <TableRow>
          {colunas.map((c) => (
            <TableCell key={c.titulo} align={c.align}>
              {c.titulo}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {linhas.map((p) => (
          <TableRow key={p.id} hover>
            {colunas.map((c) => (
              <TableCell key={c.titulo} align={c.align}>
                {c.render(p)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
)

/**
 * Uma seção da página de políticas (produto / spread / tributo): propostas pendentes com
 * Aprovar/Rejeitar (P6), vigentes em `TabelaAcao` com "Encerrar vigência", histórico (encerradas,
 * rejeitadas e ativas de início futuro) expansível e o botão de propor versão. Genérica
 * de propósito — quem chama diz as colunas de domínio; aqui só o que é comum a toda política
 * versionada (versão, situação, vigência semiaberta, origem, área dona, proponente, aprovador).
 */
export const SecaoPolitica = <T extends PoliticaVersionada>({
  titulo,
  descricao,
  rotuloNovo,
  itens,
  erro,
  colunas,
  mensagemVazio,
  onNovaVersao,
  onEncerrar,
  onTentarNovamente,
  'aria-label': ariaLabel,
  children,
  somenteLeitura = false,
  podePropor = !somenteLeitura,
  podeEncerrar = !somenteLeitura,
  podeDecidir = false,
  usuarioId = null,
  onAprovar,
  onRejeitar,
  onRetirar,
  decidindo = null,
}: SecaoPoliticaProps<T>) => {
  const [historicoAberto, setHistoricoAberto] = useState(false)
  // P6: a proposta pendente não é vigente nem histórico — tem bloco próprio, com a decisão.
  const pendentes = (itens ?? []).filter(ehPendente)
  const vigentes = (itens ?? []).filter((i) => i.vigente && !ehPendente(i))
  const historico = (itens ?? []).filter((i) => !i.vigente && !ehPendente(i))

  const colunasComuns: ColunaTabelaAcao<T>[] = [
    { titulo: 'Versão', render: (p) => <Chip size="small" label={p.versao} /> },
    { titulo: 'Situação', render: (p) => <Situacao p={p} /> },
    { titulo: 'Vigência', render: vigencia },
    { titulo: 'Origem', render: (p) => p.origem },
    { titulo: 'Área dona', render: (p) => (p.owner ? ROTULO_OWNER[p.owner] ?? p.owner : '—') },
    {
      titulo: 'Proposta por',
      render: (p) => (
        <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
          {pessoa(p.propostoPor ?? p.criadoPor, usuarioId)} · {formatDateTime(p.criadoEm)}
        </Typography>
      ),
    },
    {
      titulo: 'Aprovada por',
      render: (p) => (
        <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
          {p.aprovadoPor
            ? `${pessoa(p.aprovadoPor, usuarioId)}${p.aprovadoEm ? ` · ${formatDateTime(p.aprovadoEm)}` : ''}`
            : p.statusPublicacao === 'REJEITADA'
              ? `Rejeitada${p.rejeitadoPor ? ` por ${pessoa(p.rejeitadoPor, usuarioId)}` : ''}${p.motivoRejeicao ? `: ${p.motivoRejeicao}` : ''}`
              : '—'}
        </Typography>
      ),
    },
  ]

  const colunaDecisao: ColunaTabelaAcao<T> = {
    titulo: 'Decisão',
    render: (p) => {
      const proprio = !!usuarioId && !!p.propostoPor && p.propostoPor === usuarioId
      const ocupado = decidindo === p.id
      return (
        <Stack spacing={0.5} alignItems="flex-start">
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              disabled={proprio || ocupado || !onAprovar}
              onClick={() => onAprovar?.(p)}
            >
              Aprovar
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={proprio || ocupado || !onRejeitar}
              onClick={() => onRejeitar?.(p)}
            >
              Rejeitar
            </Button>
          </Stack>
          {proprio && (
            <Typography variant="caption" color="textSecondary">
              {TEXTO_PROPONENTE_NAO_DECIDE}
            </Typography>
          )}
          {proprio && onRetirar && (
            <Button size="small" variant="text" color="warning" disabled={ocupado} onClick={() => onRetirar(p)}>
              Retirar proposta
            </Button>
          )}
        </Stack>
      )
    },
  }

  return (
    <Card>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {titulo}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {descricao}
            </Typography>
          </Box>
          {podePropor && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={onNovaVersao} sx={{ whiteSpace: 'nowrap' }}>
              {rotuloNovo}
            </Button>
          )}
        </Stack>
        {children}

        {pendentes.length > 0 && (
          <Box sx={{ mb: 1.5 }} data-testid="propostas-pendentes">
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Propostas aguardando aprovação ({pendentes.length})
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Não valem no cálculo até serem aprovadas por outra pessoa; na aprovação a versão ativa anterior é encerrada.
            </Typography>
            <TabelaSimples<T>
              colunas={[...colunas, ...colunasComuns, ...(podeDecidir ? [colunaDecisao] : [])]}
              linhas={pendentes}
              ariaLabel={`${ariaLabel} — propostas pendentes`}
            />
          </Box>
        )}

        {itens === null && !erro ? (
          <EstadoDeLista estado="carregando" minHeight={120} />
        ) : erro ? (
          <EstadoDeLista
            estado="erro"
            detalhe={erro}
            minHeight={120}
            acao={{ label: 'Tentar novamente', onClick: onTentarNovamente }}
          />
        ) : vigentes.length === 0 ? (
          <EstadoDeLista estado="vazio" mensagem={mensagemVazio} minHeight={120} />
        ) : somenteLeitura || !podeEncerrar ? (
          <TabelaSimples<T> colunas={[...colunas, ...colunasComuns]} linhas={vigentes} ariaLabel={ariaLabel} />
        ) : (
          <TabelaAcao<T>
            aria-label={ariaLabel}
            colunas={[...colunas, ...colunasComuns]}
            linhas={vigentes}
            chaveDaLinha={(p) => p.id}
            onAbrir={onEncerrar}
            renderAcao={() => ENCERRAR_VIGENCIA}
          />
        )}

        {historico.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Button
              size="small"
              color="inherit"
              startIcon={historicoAberto ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setHistoricoAberto((v) => !v)}
              aria-expanded={historicoAberto}
            >
              Histórico ({historico.length})
            </Button>
            <Collapse in={historicoAberto} unmountOnExit>
              <TabelaSimples<T>
                colunas={[...colunas, ...colunasComuns]}
                linhas={historico}
                ariaLabel={`${ariaLabel} — histórico`}
              />
            </Collapse>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default SecaoPolitica
